import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleFormField
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { NotPermitted } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { TestInboxField, useTestInbox } from '@/domains/auth';
import { accountsApi } from '../api/accounts-api';
import type { EmailChangeReason, IAdminAccountDetail } from '../api/account-contracts';
import { neverArrived, refusalMessage, statusRefusal } from '../refusal';

/** What the press tells the page once the address moved: the page follows the account to its
 *  new address, where the card says it changed. */
export interface ILoginAddressChange {
  from: string;
  to: string;
}

const TAKEN = 'Another account has that address.';

/** Why staff change a login address (SR-487 ruling 18), in the words the card offers and the
 *  activity says. */
export const EMAIL_CHANGE_REASONS: Readonly<Record<EmailChangeReason, string>> = {
  account_update: 'Account update',
  firm_change: 'Firm change'
};

/**
 * A disabled account's address change, said where an active one's says a link is mailed:
 * survey-api's worker mints no confirmation link for a disabled account and sends none once it is
 * reactivated (SR-442's D5), while the notices go all the same. The page the card follows the
 * account to says it too.
 */
export const NO_LINK_WHILE_DISABLED =
  'No confirmation link is mailed while the account is disabled — once it is activated again, ' +
  'its owner asks for one, and the new address stays unconfirmed until then.';

/** The bodiless refusals the route can answer. A 404 is the account gone, and the page is read
 *  again at the address it opened — its "No such account" arm. */
const SET_EMAIL_REFUSALS: Record<number, string> = {
  403:
    'The system refused this — your Team Portal roles no longer include managing accounts. The ' +
    'page is read again with your current roles.',
  404: 'This account is no longer registered — the page was out of date. It has been read again.'
};

/**
 * "Email address" — `PUT /api/v1/admin/accounts/{accountId}/email` (SR-416, survey-api's B5-13):
 * staff set an advisor's address, for an advisor who lost the old mailbox or moved firm, with the
 * reason required (SR-487, survey-api's PFR §7) — "Account update" or "Firm change", carried on
 * `email_changed` and said in the activity. **The address is where
 * Veritant writes; signing in does not change** (the IdP sign-in design, decision 10): the
 * advisor signs in at Invent, and the change moves nothing there. On the user
 * card, after the roles, under `accounts.manage` (drawn off with `NotPermitted` otherwise).
 *
 * **An advisor's alone.** survey-api refuses a Team Portal account `staff-account`, so on one the
 * card is a sentence and offers no press; the 422 is still said in words for a page read before
 * the account was what it is. The hub draws no address control — its account links here.
 *
 * **A confirmed press, which says what the 204 does not**: mail stops going to the old address at
 * once, every session ends, both addresses are mailed a notice and the new one a confirmation
 * link — the new address is unconfirmed until its owner opens it. The reason is asked before the
 * press, and survey-api's 400 at `reason` is said beside it. The test inbox is judged against
 * the new address, the one the request names. An address equal to the account's own (survey-api
 * compares without case, `account.email` being `citext`) sends nothing: survey-api's answer would be
 * a 204 for nothing done.
 *
 * **What comes back**: a landing hands the page the two addresses (`onChanged`), and the page
 * follows the account to its new address; a 400 at `email` (survey-api's `problems[]`) and a 409
 * `email-taken` are said beside the box with the draft kept; the inbox's refusal is
 * `TestInboxField`'s; 422 `staff-account` is said in words; a 404 re-reads the page at the old
 * address. **An answer that never arrived** reads both addresses: the account at the new one is
 * a landing, at the old one a change that had not landed when it was read, at neither an account
 * that answers at neither — each said so, never guessed.
 */
export function LoginAddressCard({
  account,
  canManage,
  onChanged,
  onReread
}: {
  account: IAdminAccountDetail;
  /** `accounts.manage` — without it the controls are drawn off, with the reason. */
  canManage: boolean;
  onChanged: (change: ILoginAddressChange) => void;
  onReread: () => void;
}) {
  const ids = {
    email: React.useId(),
    inbox: React.useId(),
    reason: React.useId(),
    why: React.useId()
  };
  const inbox = useTestInbox();
  const [draft, setDraft] = React.useState('');
  const [why, setWhy] = React.useState<'' | EmailChangeReason>('');
  const [whyError, setWhyError] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [fieldError, setFieldError] = React.useState<Nullable<string>>(null);
  const [failure, setFailure] = React.useState<Nullable<string>>(null);
  const [note, setNote] = React.useState<Nullable<string>>(null);

  const address = draft.trim();
  const from = account.email;

  if (account.kind !== 'advisor') {
    return (
      <Card data-login-address>
        <CardHeader>
          <CardTitle>Email address</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          A Team Portal account’s address is not changed here.
        </CardContent>
      </Card>
    );
  }

  const ask = (event: React.FormEvent) => {
    event.preventDefault();
    setFailure(null);
    setNote(null);
    setFieldError(address ? null : 'The new address is required');
    setWhyError(why ? null : 'Choose a reason');
    if (!address || !why) return;
    if (address.toLowerCase() === from.toLowerCase()) {
      setNote('That is the account’s address already — nothing was sent.');
      return;
    }
    setConfirming(true);
  };

  /** The answer never arrived: which address answers for this account now decides what is said. */
  const settleUnanswered = async () => {
    const [atNew, atOld] = await Promise.allSettled([
      accountsApi.getAccountByEmail(address),
      accountsApi.getAccountByEmail(from)
    ]);
    if (atNew.status === 'fulfilled' && atNew.value?.accountId === account.accountId) {
      onChanged({ from, to: atNew.value.email });
      return;
    }
    if (atOld.status === 'fulfilled' && atOld.value?.accountId === account.accountId) {
      setFailure(
        neverArrived(
          'the change',
          `It had not landed when the page read the account again: its address is still ${from}. ` +
            'Set it again.'
        )
      );
      onReread();
      return;
    }
    if (atNew.status === 'fulfilled' && atOld.status === 'fulfilled') {
      setFailure(
        neverArrived(
          'the change',
          `Neither ${address} nor ${from} answers for this account now. Check the account list ` +
            'before setting it again.'
        )
      );
      onReread();
      return;
    }
    setFailure(
      neverArrived(
        'the change',
        `Neither ${address} nor ${from} could be read for this account. Check both before ` +
          'setting it again.'
      )
    );
  };

  const write = async () => {
    if (!why) return;
    setBusy(true);
    setFailure(null);
    inbox.begin();
    try {
      await accountsApi.setEmail(account.accountId, { email: address, reason: why, ...inbox.body });
      setConfirming(false);
      onChanged({ from, to: address });
    } catch (error) {
      setConfirming(false);
      if (inbox.refused(error, address)) return;
      if (!(error instanceof ApiError) || error.status >= 500) {
        await settleUnanswered();
      } else if (error.isProblem(ProblemType.emailTaken)) {
        setFieldError(TAKEN);
      } else if (error.isProblem(ProblemType.invalidBody)) {
        const problems = error.problem?.problems ?? [];
        const at = (target: string) =>
          problems.filter((problem) => problem.target === target).map((problem) => problem.reason);
        if (at('email').length > 0) setFieldError(at('email').join(' '));
        if (at('reason').length > 0) setWhyError(at('reason').join(' '));
        if (at('email').length + at('reason').length === 0) {
          setFailure(refusalMessage(error, 'Could not set the address'));
        }
      } else if (error.isProblem(ProblemType.staffAccount)) {
        setFailure(
          'The system sets only an Advisor Portal account’s address, and this is a Team Portal ' +
            'account. Nothing was changed.'
        );
        onReread();
      } else {
        setFailure(statusRefusal(error, SET_EMAIL_REFUSALS, 'Could not set the address'));
        if (error.status === 404) onReread();
      }
    } finally {
      setBusy(false);
    }
  };

  const disabled = busy || !canManage;

  return (
    <Card data-login-address>
      <CardHeader>
        <CardTitle>Email address</CardTitle>
        <CardDescription>
          Moves this advisor’s address to a new one — for an advisor who lost the old mailbox or
          moved firm. It is where Veritant writes; the way the advisor signs in does not change.{' '}
          {account.status === 'disabled'
            ? NO_LINK_WHILE_DISABLED
            : 'The new address is unconfirmed until its owner opens the link mailed to it.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <form noValidate className="grid gap-4 sm:max-w-[32rem]" onSubmit={ask}>
          {/* `text` with the attributes `email` would carry: whether it is one plain address is
              survey-api's to say (the sign-in identifier's rule). */}
          <SimpleFormField
            label="New address"
            htmlFor={ids.email}
            required
            error={fieldError ?? undefined}
          >
            <Input
              id={ids.email}
              type="text"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="off"
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                setNote(null);
              }}
              disabled={disabled}
              aria-invalid={fieldError !== null}
            />
          </SimpleFormField>
          <SimpleFormField label="Reason" htmlFor={ids.why} required error={whyError ?? undefined}>
            <Select
              value={why}
              onValueChange={(next) => {
                setWhy(next as EmailChangeReason);
                setWhyError(null);
              }}
              disabled={disabled}
            >
              <SelectTrigger id={ids.why} className="w-52" aria-invalid={whyError !== null}>
                <SelectValue placeholder="Choose a reason" />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(EMAIL_CHANGE_REASONS) as EmailChangeReason[]).map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {EMAIL_CHANGE_REASONS[reason]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SimpleFormField>
          <TestInboxField inbox={inbox} id={ids.inbox} intended={address} disabled={disabled} />
          <div className="grid justify-items-start gap-1">
            <Button
              type="submit"
              disabled={disabled || !inbox.settled}
              aria-describedby={canManage ? undefined : ids.reason}
            >
              {busy ? 'Changing…' : 'Change the address'}
            </Button>
            {!canManage && <NotPermitted id={ids.reason} what="changing an advisor’s address" />}
          </div>
        </form>

        {note && (
          <p role="status" className="text-sm text-muted-foreground">
            {note}
          </p>
        )}

        {failure && (
          <Alert variant="destructive">
            <AlertTitle>Not changed, or not confirmed</AlertTitle>
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}

        <AlertDialog open={confirming} onOpenChange={(open) => !busy && setConfirming(open)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change the address to {address}?</AlertDialogTitle>
              <AlertDialogDescription>
                Veritant stops writing to {from} at once. Every session of the account ends, and
                its owner signs in again as before.{' '}
                {account.status === 'disabled'
                  ? `Both addresses are mailed a notice. ${NO_LINK_WHILE_DISABLED}`
                  : 'Both addresses are mailed a notice, and the new one a confirmation link.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={busy}
                onClick={(event) => {
                  event.preventDefault();
                  void write();
                }}
              >
                {busy ? 'Changing…' : 'Change the address'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
